import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './Notes.scss';
import './Vignettes.scss';
import { useDispatch, useSelector } from "react-redux";
import * as userActions from '../../redux/actions/userActions';
import { useToasts } from 'react-toast-notifications';
import CeAssets from '../../assets/img/CeAssets.png';
import APIConstant from '../../utility/APIConstant';
import Swal from 'sweetalert2';

const Notes = () => {
    const userInfo = useSelector(state => state?.user);
    const utility = useSelector(state => state.utility);
    const { addToast } = useToasts();
    const [selection, setSelection] = useState({ course: null, ids: [] });
    const [filter, setFilter] = useState('all');
    const dispatch = useDispatch();
    const assignedAids = useSelector(state => state?.user?.assignedAids);
    // applied once per course, so it cannot stamp over changes made while the
    // request was still in flight
    const hydrated = useRef(false);
    const courseId = utility?.selectedCourseUpdates?._id;

    /* the ids are held together with the course they belong to. the poster
       list is the same for every course, so ids left over from a course we
       just left stay valid and used to be counted - and saved - against the
       new one. pairing them makes that impossible. */
    const selectedIds = useMemo(
        () => (selection.course === courseId ? selection.ids : []),
        [selection, courseId]
    );

    const setSelectedIds = useCallback((next) => {
        setSelection((prev) => {
            const base = prev.course === courseId ? prev.ids : [];
            return {
                course: courseId,
                ids: typeof next === 'function' ? next(base) : next,
            };
        });
    }, [courseId]);

    // the feed can repeat the same note, so keep the first of each id
    const Notes = useMemo(() => {
        const seen = new Set();
        return (userInfo?.supplementaries?.notes || []).filter((n) => {
            const key = n?._id;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [userInfo?.supplementaries?.notes]);

    // Load selected notes from sessionStorage on mount
    useEffect(() => {
        const savedSelections = JSON.parse(sessionStorage.getItem('vignetteSelections')) || {};
        /* always replace. this used to set only when the new course had
           something saved, so opening a course with no selection of its own
           left the previous course's ticks on screen */
        setSelectedIds(courseId ? (savedSelections[courseId]?.notes || []) : []);
        hydrated.current = false;
    }, [courseId, setSelectedIds]);

    // what is already assigned to this config, so the selection survives a
    // reload rather than only lasting the session
    useEffect(() => {
        if (courseId) dispatch(userActions.assignedAidsRequest(courseId));
    }, [courseId, dispatch]);

    useEffect(() => {
        if (hydrated.current) return;
        if (!assignedAids?.fetched || assignedAids.configId !== courseId) return;
        if (!Notes.length) return;

        /* the saved rows carry no _id, so match them back on path. the feed
           can hold several posters pointing at the same file, so take one
           card per saved row instead of every card sharing the path -
           otherwise one saved selection ticked two cards and the count
           doubled. */
        const saved = (assignedAids.notes || [])
            .map((n) => n?.path)
            .filter(Boolean);

        const byPath = new Map();
        Notes.forEach((n) => {
            const p = n?.content?.path;
            if (!p) return;
            if (!byPath.has(p)) byPath.set(p, []);
            byPath.get(p).push(n._id);
        });

        const taken = new Map();
        const ids = [];
        saved.forEach((p) => {
            const pool = byPath.get(p);
            if (!pool) return;
            const used = taken.get(p) || 0;
            if (used < pool.length) {
                ids.push(pool[used]);
                taken.set(p, used + 1);
            }
        });

        if (ids.length) setSelectedIds(ids);
        hydrated.current = true;
    }, [assignedAids, courseId, Notes, setSelectedIds]);

    // ids outlive the list they came from - session storage keeps the last
    // selection and the feed can change under it - so drop any id that has no
    // card. without this the count overstates the selection and Save posts ids
    // that are no longer real.
    useEffect(() => {
        if (!Notes.length) return;
        const live = new Set(Notes.map((n) => n?._id));
        setSelectedIds((prev) => {
            const next = Array.from(new Set(prev)).filter((id) => live.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [Notes, setSelectedIds]);

    // Save selected notes to sessionStorage on change
    useEffect(() => {
        /* only write ids that belong to this course. on a course switch the
           reset above has not landed yet, so the state still holds the old
           course's ids while courseId is already the new one - writing then
           filed the previous course's selection under the new course */
        if (!courseId || selection.course !== courseId) return;
        const savedSelections = JSON.parse(sessionStorage.getItem('vignetteSelections')) || {};
        savedSelections[courseId] = {
            ...savedSelections[courseId],
            notes: selection.ids
        };
        sessionStorage.setItem('vignetteSelections', JSON.stringify(savedSelections));
    }, [selection, courseId]);

    const handleSelect = (id) => {
        setSelectedIds((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter(itemId => itemId !== id); // Deselect
            }
            return [...prevSelected, id]; // Select
        });
    };

    /* with 130+ cards the only way to review a selection was to scroll the
       whole list looking for ticks */
    const visibleNotes = useMemo(() => {
        if (filter === 'all') return Notes;
        const picked = new Set(selectedIds);
        return Notes.filter((n) =>
            filter === 'selected' ? picked.has(n?._id) : !picked.has(n?._id)
        );
    }, [Notes, selectedIds, filter]);

    const allSelected = Notes.length > 0 && selectedIds.length === Notes.length;

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedIds([]); // Deselect all
            const savedSelections = JSON.parse(sessionStorage.getItem('vignetteSelections')) || {};
            savedSelections[courseId] = {
                ...savedSelections[courseId],
                notes: []
            };
            sessionStorage.setItem('vignetteSelections', JSON.stringify(savedSelections));
        } else {
            setSelectedIds(Notes.map(note => note._id)); // Select all
        }
    };

    const handleSave = async () => {
        /* an empty save clears the course, so make that explicit rather than
           locking the button - without it there was no way to unassign
           everything once something had been assigned */
        if (!selectedIds.length) {
            const confirmed = await Swal.fire({
                title: 'Remove all posters?',
                text: 'Nothing is selected, so saving clears every poster assigned to this course.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, clear them',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#d33',
            });
            if (!confirmed.isConfirmed) return;
        }
        let reqObj = {
            "configid": courseId,
            "vids": selectedIds,
            "type": 10
        };
        dispatch(userActions.addConfigVignettesRequest(reqObj, addToast));
    };

    return (
        <div className='notes-continer'>
            {Notes?.length > 0 ? (
                <div className="vg-panel">
                    {/* stays put while the grid below scrolls */}
                    <div className="vg-bar">
                        <span className="vg-count">
                            {selectedIds.length
                                ? <><b>{selectedIds.length}</b> of {Notes.length} selected</>
                                : <>{Notes.length} posters</>}
                        </span>
                        <span className="vg-filter">
                            {[
                                { key: 'all', label: `All (${Notes.length})` },
                                { key: 'selected', label: `Selected (${selectedIds.length})` },
                                { key: 'unselected', label: `Unselected (${Notes.length - selectedIds.length})` },
                            ].map((f) => (
                                <button
                                    key={f.key}
                                    type="button"
                                    className={filter === f.key ? 'on' : ''}
                                    onClick={() => setFilter(f.key)}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </span>
                        <button
                            className={`vg-select-all ${allSelected ? 'on' : ''}`}
                            onClick={handleSelectAll}
                            type="button"
                        >
                            {allSelected ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>

                    <div className="vg-scroll">
                        <div className="vg-grid vg-grid-tall">
                            {!visibleNotes.length && (
                                <p className="vg-empty-filter">
                                    {filter === 'selected'
                                        ? 'Nothing selected yet.'
                                        : 'Everything is selected.'}
                                </p>
                            )}
                            {visibleNotes.map((item) => {
                                const isSelected = selectedIds.includes(item._id);
                                return (
                                    <div
                                        key={item._id}
                                        className={`vg-card ${isSelected ? 'selected' : ''}`}
                                        onClick={() => handleSelect(item._id)}
                                        role="checkbox"
                                        aria-checked={isSelected}
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleSelect(item._id);
                                            }
                                        }}
                                    >
                                        <div className="vg-thumb vg-thumb-tall">
                                            <img
                                                src={APIConstant.getFilePath(item?.content?.path)}
                                                alt=""
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = CeAssets;
                                                }}
                                            />
                                            {isSelected && <span className="vg-check">&#10003;</span>}
                                        </div>
                                        <p className="vg-title" title={item?.content?.title}>
                                            {item?.content?.title}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* stays put so Save is always one click away */}
                    <div className="vg-foot">
                        <span className="vg-foot-note">
                            {selectedIds.length
                                ? `${selectedIds.length} selected`
                                : 'Nothing selected - saving clears every assigned poster'}
                        </span>
                        <button
                            className="vg-save"
                            disabled={!Notes.length}
                            onClick={handleSave}
                            type="button"
                        >
                            Save
                        </button>
                    </div>
                </div>
            ) : (
                <p className='no-data'>Currently there are no posters available...</p>
            )}
        </div>
    );
};

export default Notes;
